import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Link, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader";
// eslint-disable-next-line
import Auth from "j-toker";
import $ from "jquery";

/** Styles */
import "./register.scss";

/** Icons */
import { BsEye, BsEyeSlash } from "react-icons/bs";

/** Assets */
import PageBg from "../../../src/assets/images/auth-bg.png";
import Logo from "../../../src/assets/images/logo.png";

/** Redux */
import { set_user } from "../../redux/actions/AuthAction";
import { set_page_state } from "../../redux/actions/PageStateAction";
import { set_store } from "../../redux/actions/CreateStoreAction";

/** Auth */
import { signUp } from "../../jtoker";

const registerSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .matches(/[A-Z]/, "Must contain at least one uppercase letter")
    .matches(/[0-9]/, "Must contain at least one number")
    .matches(/[!@#$%^&*]/, "Must contain at least one special character")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords do not match")
    .required("Please confirm your password"),
  agreeToTerms: Yup.boolean()
    .oneOf([true], "You must accept the terms & conditions")
    .required("You must accept the terms & conditions"),
});

const RegisterPage = (props) => {
  const { history } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const unique_code = queryParams.get("unique_code");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  let message = "";

  useEffect(() => {
    document.title = "Register";
  }, []);

  const showToastError = () => {
    toast.error(message, {
      position: "bottom-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const showToastSuccess = () => {
    toast.success(message, {
      position: "bottom-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
    },
    validationSchema: registerSchema,
    onSubmit: (values) => {
      setLoading(true);

      signUp({
        email: values.email,
        password: values.password,
        confirm_password: values.confirmPassword,
        unique_code: unique_code,
      })
        .then(() => {
          localStorage.setItem(
            "access_token",
            $.auth.retrieveData("authHeaders")["access-token"],
          );
          localStorage.setItem(
            "client",
            $.auth.retrieveData("authHeaders").client,
          );
          localStorage.setItem("uid", $.auth.retrieveData("authHeaders").uid);
          props.set_user({ isSignedIn: true, user: $.auth.user });
          props.set_page_state({ pageState: { id: 2 } });
          message = "Successfully signed up!";
          showToastSuccess();
          setLoading(false);
          history.replace("/vendor/onboarding");
        })
        .fail((error) => {
          setLoading(false);
          if (!error.data.errors) {
            message = "Network Error!";
            showToastError();
          } else if (
            error.data?.errors?.email?.[0]?.toLowerCase() ===
            "has already been taken"
          ) {
            message = `${error.data.errors.full_messages[0]}. Login to continue`;
            showToastError();
            history.replace("/");
          } else {
            message = error.data.errors.full_messages[0];
            showToastError();
          }
        });
    },
  });

  return (
    <div
      className="register-wrapper"
      style={{ backgroundImage: `url(${PageBg})` }}
    >
      <div className="register-overlay" />

      <Container maxWidth="sm" className="register-container">
        <Paper
          elevation={6}
          className={`register-paper ${isMobile ? "mobile" : ""}`}
        >
          <Box className="register-logo">
            <img src={Logo} alt="Logo" className={isMobile ? "mobile" : ""} />
          </Box>

          <Typography align="center" gutterBottom className="register-title">
            Create an account
          </Typography>

          <form onSubmit={formik.handleSubmit} className="register-form">
            <Box className={`fields ${isMobile ? "mobile" : ""}`}>
              {/* Email */}
              <TextField
                name="email"
                type="email"
                placeholder="Enter your email address"
                fullWidth
                variant="outlined"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />

              {/* Password */}
              <TextField
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                fullWidth
                variant="outlined"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? (
                          <BsEye size="1.5em" />
                        ) : (
                          <BsEyeSlash size="1.5em" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Confirm Password */}
              <TextField
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your password"
                fullWidth
                variant="outlined"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? (
                          <BsEye size="1.5em" />
                        ) : (
                          <BsEyeSlash size="1.5em" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password strength hints */}
              <Box className="password-rules">
                <Typography
                  className={`rule ${formik.values.password.length >= 6 ? "valid" : ""}`}
                >
                  ✓ At least 6 characters
                </Typography>
                <Typography
                  className={`rule ${/[A-Z]/.test(formik.values.password) ? "valid" : ""}`}
                >
                  ✓ One uppercase letter
                </Typography>
                <Typography
                  className={`rule ${/[0-9]/.test(formik.values.password) ? "valid" : ""}`}
                >
                  ✓ One number
                </Typography>
                <Typography
                  className={`rule ${/[!@#$%^&*]/.test(formik.values.password) ? "valid" : ""}`}
                >
                  ✓ One special character (!@#$%^&*)
                </Typography>
              </Box>

              {/* Terms */}
              <Box className="terms-wrapper">
                <FormControlLabel
                  control={
                    <Checkbox
                      name="agreeToTerms"
                      checked={formik.values.agreeToTerms}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      color="primary"
                    />
                  }
                  label={
                    <Typography className="terms-label">
                      I agree to the{" "}
                      <a
                        href="https://alisouq.com/terms-and-conditions"
                        style={{ color: theme.palette.primary.main }}
                        target="_blank"
                      >
                        Terms & Conditions
                      </a>
                    </Typography>
                  }
                />
                {formik.touched.agreeToTerms && formik.errors.agreeToTerms && (
                  <FormHelperText error>
                    {formik.errors.agreeToTerms}
                  </FormHelperText>
                )}
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size={isMobile ? "medium" : "large"}
                fullWidth
                disabled={loading}
              >
                {loading ? (
                  <ClipLoader color="#fff" loading size={20} />
                ) : (
                  "Register"
                )}
              </Button>
            </Box>
          </form>

          <Typography align="center" className="register-footer">
            Already have an account?{" "}
            <Link to="/" style={{ color: theme.palette.primary.main }}>
              Log in
            </Link>
          </Typography>
        </Paper>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  width: state.layout.screenWidth,
});

const mapDispatchToProps = {
  set_user,
  set_page_state,
  set_store,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(RegisterPage),
);
